import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Pet } from "../types/PetTypes";
import { loadPets, savePets } from "../utils/localStorage";
import PetContext from "./PetContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logger } from "@/utils/logger";

// Map a raw Supabase pets row to the local Pet type
function mapRowToPet(row: Record<string, unknown>): Pet {
  return {
    id: row.id as string,
    name: row.name as string,
    species: row.species as string,
    breed: (row.breed as string) || "",
    dateOfBirth: (row.date_of_birth as string) || "",
    gender: row.gender as "male" | "female" | undefined,
    photoUrl: (row.photo_url as string) || "",
    weight: (row.weight as number) || undefined,
    microchip: (row.chip_number as string) || "",
    chipNumber: (row.chip_number as string) || undefined,
    color: (row.color as string) || undefined,
    isNeutered: (row.is_neutered as boolean) ?? false,
    publicId: (row.public_id as string) || undefined,
    publicContactName: (row.public_contact_name as string) || undefined,
    publicContactPhone: (row.public_contact_phone as string) || undefined,
    publicContactEmail: (row.public_contact_email as string) || undefined,
    publicContactAddress: (row.public_contact_address as string) || undefined,
    isActive: (row.is_active as boolean) ?? true,
    notes: (row.notes as string) || "",
    user_id: row.user_id as string,
  };
}

// PetProvider component
const PetProviderComponent: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const [pets, setPets] = useState<Pet[]>(() => loadPets());
  const [activePet, setActivePet] = useState<Pet | undefined>();
  // Only show loading skeleton when no cached data — avoids blank screen on every navigation
  const [isLoading, setIsLoading] = useState(() => loadPets().length === 0);

  // Load pets from Supabase on mount
  useEffect(() => {
    const loadPetsFromSupabase = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const user = session?.user ?? null;
        if (!user) {
          setIsLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from("pets")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });

        if (error) {
          logger.error("Error loading pets from Supabase:", error);
          setIsLoading(false);
          return;
        }

        if (data) {
          const supabasePets = data.map((pet) =>
            mapRowToPet(pet as unknown as Record<string, unknown>),
          );
          setPets(supabasePets);
          savePets(supabasePets);
        }
      } catch (error) {
        logger.error("Error in loadPetsFromSupabase:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPetsFromSupabase();
  }, []);

  // Realtime subscription — sync changes from other devices/tabs
  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setupRealtime = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return;

      channel = supabase
        .channel(`pets-realtime-${userId}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "pets",
            filter: `user_id=eq.${userId}`,
          },
          (payload) => {
            if (payload.eventType === "INSERT") {
              const newPet = mapRowToPet(
                payload.new as Record<string, unknown>,
              );
              setPets((current) => {
                if (current.some((p) => p.id === newPet.id)) return current;
                const updated = [newPet, ...current];
                savePets(updated);
                return updated;
              });
            } else if (payload.eventType === "UPDATE") {
              const updatedPet = mapRowToPet(
                payload.new as Record<string, unknown>,
              );
              setPets((current) => {
                const updated = current.map((p) =>
                  p.id === updatedPet.id ? updatedPet : p,
                );
                savePets(updated);
                return updated;
              });
            } else if (payload.eventType === "DELETE") {
              const deletedId = (payload.old as Record<string, unknown>)
                .id as string;
              setPets((current) => {
                const updated = current.filter((p) => p.id !== deletedId);
                savePets(updated);
                return updated;
              });
            }
          },
        )
        .subscribe((status) => {
          if (status === "CHANNEL_ERROR") {
            logger.error("Pets realtime channel error");
          }
        });
    };

    setupRealtime();

    return () => {
      if (channel) supabase.removeChannel(channel);
    };
  }, []);

  // Synchronize activePet with pets
  useEffect(() => {
    const currentActive = pets.find((pet) => pet.isActive);
    if (currentActive?.id !== activePet?.id) {
      setActivePet(currentActive);
    }
  }, [pets, activePet?.id]);

  useEffect(() => {
    savePets(pets);
  }, [pets]);

  // Add pet - BACKEND-FIRST: Supabase génère l'UUID, trigger vérifie la limite
  // AUCUN fallback local, AUCUN UUID client, ERREUR BLOQUANTE si offline
  const addPet = useCallback(
    async (pet: Omit<Pet, "id">): Promise<Pet | null> => {
      // VÉRIFICATION CONNEXION RÉSEAU - BLOQUANT
      if (!navigator.onLine) {
        toast.error("Connexion internet requise", {
          description:
            "La création d'animaux nécessite une connexion pour être enregistrée de manière sécurisée. Aucun mode hors-ligne n'est disponible pour cette opération.",
        });
        return null;
      }

      try {
        // VÉRIFICATION AUTHENTIFICATION - BLOQUANT
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const user = session?.user ?? null;
        if (!user) {
          toast.error("Vous devez être connecté pour ajouter un animal", {
            description: "Connectez-vous ou créez un compte pour continuer.",
          });
          return null;
        }

        // INSERT SUPABASE - Le backend génère l'UUID via gen_random_uuid()
        // Le trigger enforce_pets_limit vérifie la limite AVANT l'insertion
        const { data, error } = await supabase
          .from("pets")
          .insert({
            name: pet.name,
            species: pet.species,
            breed: pet.breed || null,
            date_of_birth: pet.dateOfBirth || null,
            gender: pet.gender || null,
            photo_url: pet.photoUrl || null,
            weight: pet.weight || null,
            chip_number: pet.chipNumber || null,
            is_active: true,
            notes: pet.notes || null,
            user_id: user.id,
          })
          .select()
          .single();

        if (error) {
          // Gestion spécifique de l'erreur limite backend
          if (
            error.message.includes("Limite de") ||
            error.message.includes("limite")
          ) {
            toast.error("Limite d'animaux atteinte", {
              description: error.message,
              action: {
                label: "S'abonner",
                onClick: () => navigate("/subscription"),
              },
            });
          } else {
            logger.error("Error adding pet:", error);
            toast.error("Erreur lors de l'ajout de l'animal", {
              description: error.message,
            });
          }
          return null;
        }

        // IMPORTANT: L'ID vient de Supabase, pas du client
        const newPet: Pet = {
          id: data.id, // UUID généré par PostgreSQL
          name: data.name,
          species: data.species,
          breed: data.breed || "",
          dateOfBirth: data.date_of_birth || "",
          gender: data.gender as "male" | "female" | undefined,
          photoUrl: data.photo_url || "",
          weight: data.weight || undefined,
          chipNumber: data.chip_number || "",
          isActive: data.is_active ?? true,
          notes: data.notes || "",
          user_id: data.user_id,
        };

        // Mise à jour du state React UNIQUEMENT après succès Supabase
        // PAS de savePets() ici - la source de vérité est Supabase
        setPets((currentPets) => [...currentPets, newPet]);

        toast.success("Animal ajouté avec succès");
        return newPet;
      } catch (error) {
        logger.error("Error in addPet:", error);
        toast.error("Erreur lors de l'ajout de l'animal", {
          description:
            "Une erreur inattendue s'est produite. Veuillez réessayer.",
        });
        return null;
      }
    },
    [navigate],
  );

  // Update pet - synchronized with Supabase
  const updatePet = useCallback(async (id: string, updates: Partial<Pet>) => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      if (!user) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dbUpdates: Record<string, any> = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.species !== undefined) dbUpdates.species = updates.species;
      if (updates.breed !== undefined) dbUpdates.breed = updates.breed;
      if (updates.dateOfBirth !== undefined)
        dbUpdates.date_of_birth = updates.dateOfBirth;
      if (updates.gender !== undefined) dbUpdates.gender = updates.gender;
      if (updates.photoUrl !== undefined)
        dbUpdates.photo_url = updates.photoUrl;
      if (updates.weight !== undefined) dbUpdates.weight = updates.weight;
      if (updates.chipNumber !== undefined)
        dbUpdates.chip_number = updates.chipNumber;
      if (updates.isActive !== undefined)
        dbUpdates.is_active = updates.isActive;
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes;

      const { error } = await supabase
        .from("pets")
        .update(dbUpdates)
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) {
        logger.error("Error updating pet in Supabase:", error);
        toast.error("Erreur lors de la mise à jour");
        return;
      }

      setPets((currentPets) => {
        const updatedPets = currentPets.map((pet) =>
          pet.id === id ? { ...pet, ...updates } : pet,
        );
        savePets(updatedPets);
        return updatedPets;
      });
    } catch (error) {
      logger.error("Error in updatePet:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  }, []);

  // Delete pet - synchronized with Supabase (cascades to related data)
  const deletePet = useCallback(async (id: string) => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      if (!user) return;

      const { error } = await supabase
        .from("pets")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) {
        logger.error("Error deleting pet from Supabase:", error);
        toast.error("Erreur lors de la suppression");
        return;
      }

      setPets((currentPets) => {
        const updatedPets = currentPets.filter((pet) => pet.id !== id);
        savePets(updatedPets);
        return updatedPets;
      });

      toast.success("Animal supprimé");
    } catch (error) {
      logger.error("Error in deletePet:", error);
      toast.error("Erreur lors de la suppression");
    }
  }, []);

  // Toggle pet active status
  const togglePetActive = useCallback(async (id: string, isActive: boolean) => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const user = session?.user ?? null;
      if (!user) return;

      const { error } = await supabase
        .from("pets")
        .update({ is_active: isActive })
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) {
        logger.error("Error toggling pet active:", error);
        toast.error("Impossible de modifier le statut de l'animal");
        return;
      }

      setPets((currentPets) => {
        const updatedPets = currentPets.map((pet) =>
          pet.id === id ? { ...pet, isActive: isActive } : pet,
        );
        savePets(updatedPets);
        return updatedPets;
      });
    } catch (error) {
      logger.error("Error in togglePetActive:", error);
      toast.error("Impossible de modifier le statut de l'animal");
    }
  }, []);

  const getPetById = useCallback(
    (id: string): Pet | undefined => {
      return pets.find((pet) => pet.id === id);
    },
    [pets],
  );

  const contextValue = useMemo(
    () => ({
      // Pet management
      pets,
      setPets,
      addPet,
      updatePet,
      deletePet,
      togglePetActive,
      activePet,
      setActivePet,
      getPetById,
      isLoading,
    }),
    [
      pets,
      addPet,
      updatePet,
      deletePet,
      togglePetActive,
      activePet,
      getPetById,
      isLoading,
    ],
  );

  return (
    <PetContext.Provider value={contextValue}>{children}</PetContext.Provider>
  );
};

export const PetProvider = PetProviderComponent;

export default PetProvider;
